import React from 'react';
import {
  AbsoluteFill,
  useVideoConfig,
  useCurrentFrame,
  interpolate,
  spring,
} from 'remotion';

/**
 * 抛物线运动模板
 *
 * 功能：演示抛物线运动轨迹
 * 应用：高中物理教学（力学）
 *
 * 参数：
 * - v0: 初速度（m/s）
 * - angle: 发射角度（度）
 * - g: 重力加速度（m/s²）
 * - showVectors: 是否显示速度向量分解
 * - showTrajectory: 是否显示轨迹线
 * - showMaxHeight: 是否显示最高点
 * - showRange: 是否显示射程
 */

interface Props {
  v0?: number;
  angle?: number;
  g?: number;
  showVectors?: boolean;
  showTrajectory?: boolean;
  showMaxHeight?: boolean;
  showRange?: boolean;
  animationDuration?: number;
  colors?: {
    background: string;
    ground: string;
    trajectory: string;
    ball: string;
    vector: string;
    text: string;
    highlight: string;
  };
}

export const ProjectileMotionTemplate: React.FC<Props> = ({
  v0 = 20,
  angle = 45,
  g = 9.8,
  showVectors = true,
  showTrajectory = true,
  showMaxHeight = true,
  showRange = true,
  animationDuration = 5,
  colors = {
    background: '#F0F9FF',
    ground: '#059669',
    trajectory: '#3B82F6',
    ball: '#EF4444',
    vector: '#F59E0B',
    text: '#1F2937',
    highlight: '#10B981',
  },
}) => {
  const frame = useCurrentFrame();
  const { fps, width, height } = useVideoConfig();

  // 坐标系设置
  const groundY = height - 120;
  const originX = 180;
  const scale = 15; // 每米 15 像素

  // 物理计算
  const angleRad = (angle * Math.PI) / 180;
  const vx = v0 * Math.cos(angleRad);
  const vy = v0 * Math.sin(angleRad);

  // 运动参数
  const totalTime = (2 * vy) / g;
  const maxHeight = (vy * vy) / (2 * g);
  const range = vx * totalTime;

  // 动画进度
  const totalFrames = animationDuration * fps;
  const progress = interpolate(frame, [0, totalFrames], [0, 1], {
    extrapolateRight: 'clamp',
  });
  const currentTime = progress * totalTime * 1.05;
  const clampedT = Math.min(currentTime, totalTime);

  // 位置计算
  const calculatePosition = (t: number): { x: number; y: number } | null => {
    if (t < 0 || t > totalTime) return null;
    const x = vx * t;
    const y = vy * t - 0.5 * g * t * t;
    return {
      x: originX + x * scale,
      y: groundY - y * scale,
    };
  };

  // 生成轨迹路径（已走过的部分）
  const generateTrajectoryPath = (upToT: number): string => {
    const points: string[] = [];
    const steps = 120;
    const maxSteps = Math.floor((upToT / totalTime) * steps);
    for (let i = 0; i <= maxSteps; i++) {
      const t = (i / steps) * totalTime;
      const pos = calculatePosition(t);
      if (pos) {
        points.push(`${i === 0 ? 'M' : 'L'} ${pos.x} ${pos.y}`);
      }
    }
    return points.join(' ');
  };

  // 生成完整轨迹（幽灵预览）
  const generateFullGhostPath = (): string => {
    const points: string[] = [];
    const steps = 120;
    for (let i = 0; i <= steps; i++) {
      const t = (i / steps) * totalTime;
      const pos = calculatePosition(t);
      if (pos) {
        points.push(`${i === 0 ? 'M' : 'L'} ${pos.x} ${pos.y}`);
      }
    }
    return points.join(' ');
  };

  // 当前小球位置
  const ballPos = calculatePosition(clampedT);

  // 当前速度分量
  const currentVy = vy - g * clampedT;
  const currentVx = vx;
  const currentSpeed = Math.sqrt(currentVx * currentVx + currentVy * currentVy);

  // 关键点
  const maxTime = vy / g;
  const maxHeightPos = calculatePosition(maxTime);
  const landingPos = calculatePosition(totalTime);

  // 阶段判断
  const phase: 'launch' | 'rising' | 'peak' | 'falling' | 'landed' =
    clampedT <= 0
      ? 'launch'
      : clampedT >= totalTime
      ? 'landed'
      : Math.abs(clampedT - maxTime) < 0.08
      ? 'peak'
      : clampedT < maxTime
      ? 'rising'
      : 'falling';

  const phaseLabel = {
    launch: '发射准备',
    rising: '上升阶段',
    peak: '到达最高点',
    falling: '下降阶段',
    landed: '落地',
  }[phase];

  const phaseColor = {
    launch: colors.vector,
    rising: '#0EA5E9',
    peak: colors.highlight,
    falling: '#8B5CF6',
    landed: colors.ball,
  }[phase];

  // 入场动画
  const titleOpacity = interpolate(frame, [0, fps * 0.6], [0, 1], {
    extrapolateRight: 'clamp',
  });
  const sceneOpacity = interpolate(frame, [fps * 0.3, fps * 0.9], [0, 1], {
    extrapolateRight: 'clamp',
  });
  const panelSpring = spring({ frame: frame - fps * 0.4, fps, config: { damping: 14 } });

  // 拖尾残影（取若干历史点）
  const trailPositions: Array<{ x: number; y: number; opacity: number }> = [];
  const trailCount = 8;
  for (let i = 1; i <= trailCount; i++) {
    const t = clampedT - i * 0.06;
    const p = calculatePosition(t);
    if (p) trailPositions.push({ ...p, opacity: (1 - i / trailCount) * 0.45 });
  }

  // 落地冲击波（落地后 0.6s 内）
  const landedElapsed = Math.max(0, currentTime - totalTime);
  const showImpact = landedElapsed > 0 && landedElapsed < 0.6;
  const impactProgress = Math.min(1, landedElapsed / 0.6);

  // 初始发射角弧线半径
  const arcRadius = 70;
  const arcSweep = Math.min(1, frame / (fps * 0.8));

  // 合速度向量方向
  const speedAngle = Math.atan2(-currentVy, currentVx); // 屏幕坐标 y 向下
  const vScale = scale * 0.9;
  const resultantEnd = ballPos && {
    x: ballPos.x + currentVx * vScale,
    y: ballPos.y + (-currentVy) * vScale, // 注意方向
  };

  // 网格刻度（米）
  const gridMeters: number[] = [];
  for (let m = 5; m * scale + originX < width - 40; m += 5) gridMeters.push(m);
  const vGridMeters: number[] = [];
  for (let m = 5; groundY - m * scale > 80; m += 5) vGridMeters.push(m);

  return (
    <AbsoluteFill
      style={{
        background: `linear-gradient(180deg, #DBEAFE 0%, ${colors.background} 55%, #E0F2FE 100%)`,
        justifyContent: 'center',
        alignItems: 'center',
        fontFamily: 'system-ui, -apple-system, sans-serif',
      }}
    >
      {/* 太阳光晕 */}
      <div
        style={{
          position: 'absolute',
          top: 80,
          right: 180,
          width: 160,
          height: 160,
          borderRadius: '50%',
          background:
            'radial-gradient(circle, rgba(253, 224, 71, 0.9) 0%, rgba(253, 224, 71, 0.3) 40%, transparent 70%)',
          opacity: sceneOpacity * 0.85,
        }}
      />

      {/* 标题 */}
      <div
        style={{
          position: 'absolute',
          top: 50,
          width: '100%',
          textAlign: 'center',
          opacity: titleOpacity,
          fontSize: 56,
          fontWeight: 800,
          color: colors.text,
          letterSpacing: 4,
          textShadow: '0 2px 8px rgba(255,255,255,0.8)',
        }}
      >
        抛物线运动
      </div>
      <div
        style={{
          position: 'absolute',
          top: 124,
          width: '100%',
          textAlign: 'center',
          opacity: titleOpacity * 0.75,
          fontSize: 24,
          color: colors.text,
          letterSpacing: 6,
        }}
      >
        Projectile Motion · 二维斜抛运动
      </div>

      {/* 左上：参数面板 */}
      <div
        style={{
          position: 'absolute',
          top: 180,
          left: 230,
          fontSize: 22,
          color: colors.text,
          background: 'rgba(255,255,255,0.7)',
          backdropFilter: 'blur(6px)',
          padding: '16px 22px',
          borderRadius: 14,
          border: '1px solid rgba(59,130,246,0.25)',
          boxShadow: '0 6px 20px rgba(0,0,0,0.06)',
          transform: `translateX(${(1 - panelSpring) * -40}px)`,
          opacity: panelSpring,
          lineHeight: 1.8,
        }}
      >
        <div style={{ fontSize: 20, color: colors.trajectory, fontWeight: 700, letterSpacing: 2 }}>
          INITIAL CONDITIONS
        </div>
        <div>初速度 v₀ = <b style={{ color: colors.trajectory }}>{v0}</b> m/s</div>
        <div>发射角 θ = <b style={{ color: colors.vector }}>{angle}°</b></div>
        <div>重力加速度 g = <b>{g}</b> m/s²</div>
      </div>

      {/* 右上：实时数据 */}
      {ballPos && (
        <div
          style={{
            position: 'absolute',
            top: 170,
            right: 60,
            fontSize: 24,
            color: colors.text,
            background: 'rgba(255,255,255,0.7)',
            backdropFilter: 'blur(6px)',
            padding: '16px 22px',
            borderRadius: 14,
            border: '1px solid rgba(239,68,68,0.25)',
            boxShadow: '0 6px 20px rgba(0,0,0,0.06)',
            textAlign: 'right',
            transform: `translateX(${(1 - panelSpring) * 40}px)`,
            opacity: panelSpring,
            lineHeight: 1.8,
            minWidth: 260,
          }}
        >
          <div style={{ fontSize: 20, color: colors.ball, fontWeight: 700, letterSpacing: 2 }}>
            REAL-TIME · {phaseLabel}
          </div>
          <div>t = <b>{clampedT.toFixed(2)}</b> s</div>
          <div>
            h = <b>{Math.max(0, vy * clampedT - 0.5 * g * clampedT * clampedT).toFixed(1)}</b> m
          </div>
          <div>x = <b>{(vx * clampedT).toFixed(1)}</b> m</div>
          <div>
            |v| = <b style={{ color: colors.vector }}>{currentSpeed.toFixed(1)}</b> m/s
          </div>
        </div>
      )}

      {/* 主画布 */}
      <svg width={width} height={height} style={{ position: 'absolute', opacity: sceneOpacity }}>
        <defs>
          <linearGradient id="trajectoryGrad" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={colors.trajectory} stopOpacity="0.9" />
            <stop offset="50%" stopColor={colors.highlight} stopOpacity="0.9" />
            <stop offset="100%" stopColor={colors.ball} stopOpacity="0.9" />
          </linearGradient>
          <linearGradient id="groundGrad" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={colors.ground} stopOpacity="0.55" />
            <stop offset="100%" stopColor={colors.ground} stopOpacity="0.2" />
          </linearGradient>
          <radialGradient id="ballGrad">
            <stop offset="0%" stopColor="#FEE2E2" />
            <stop offset="40%" stopColor={colors.ball} />
            <stop offset="100%" stopColor="#991B1B" />
          </radialGradient>
          <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="4" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <marker
            id="arrowVec"
            viewBox="0 0 10 10"
            refX="9"
            refY="5"
            markerWidth="8"
            markerHeight="8"
            orient="auto-start-reverse"
          >
            <path d="M 0 0 L 10 5 L 0 10 z" fill={colors.vector} />
          </marker>
          <marker
            id="arrowResult"
            viewBox="0 0 10 10"
            refX="9"
            refY="5"
            markerWidth="10"
            markerHeight="10"
            orient="auto-start-reverse"
          >
            <path d="M 0 0 L 10 5 L 0 10 z" fill="#DC2626" />
          </marker>
        </defs>

        {/* 网格 */}
        <g opacity={0.18}>
          {gridMeters.map((m) => (
            <line
              key={`gx-${m}`}
              x1={originX + m * scale}
              y1={80}
              x2={originX + m * scale}
              y2={groundY}
              stroke={colors.text}
              strokeWidth={1}
              strokeDasharray="2,4"
            />
          ))}
          {vGridMeters.map((m) => (
            <line
              key={`gy-${m}`}
              x1={originX}
              y1={groundY - m * scale}
              x2={width - 40}
              y2={groundY - m * scale}
              stroke={colors.text}
              strokeWidth={1}
              strokeDasharray="2,4"
            />
          ))}
        </g>

        {/* 地面 */}
        <rect
          x={0}
          y={groundY}
          width={width}
          height={height - groundY}
          fill="url(#groundGrad)"
        />
        {/* 草地纹理小短线 */}
        {Array.from({ length: 60 }).map((_, i) => {
          const gx = (i / 60) * width;
          const gh = 6 + ((i * 37) % 8);
          return (
            <line
              key={`grass-${i}`}
              x1={gx}
              y1={groundY}
              x2={gx + 2}
              y2={groundY - gh}
              stroke={colors.ground}
              strokeWidth={1.5}
              opacity={0.5}
            />
          );
        })}

        {/* 坐标轴 */}
        <line
          x1={originX}
          y1={groundY}
          x2={width - 40}
          y2={groundY}
          stroke={colors.text}
          strokeWidth={2.5}
        />
        <line
          x1={originX}
          y1={groundY}
          x2={originX}
          y2={60}
          stroke={colors.text}
          strokeWidth={2.5}
        />
        <polygon
          points={`${width - 40},${groundY - 6} ${width - 28},${groundY} ${width - 40},${groundY + 6}`}
          fill={colors.text}
        />
        <polygon
          points={`${originX - 6},${60 + 12} ${originX},${60} ${originX + 6},${60 + 12}`}
          fill={colors.text}
        />
        <text x={width - 30} y={groundY - 10} fontSize={22} fill={colors.text} fontWeight={700}>
          x
        </text>
        <text x={originX + 10} y={58} fontSize={22} fill={colors.text} fontWeight={700}>
          y
        </text>

        {/* 刻度 */}
        {gridMeters.map((m) => (
          <g key={`tx-${m}`}>
            <line
              x1={originX + m * scale}
              y1={groundY - 4}
              x2={originX + m * scale}
              y2={groundY + 4}
              stroke={colors.text}
              strokeWidth={1.5}
            />
            {m % 10 === 0 && (
              <text
                x={originX + m * scale - 6}
                y={groundY + 22}
                fontSize={16}
                fill={colors.text}
                opacity={0.65}
              >
                {m}
              </text>
            )}
          </g>
        ))}
        {vGridMeters.map((m) => (
          <g key={`ty-${m}`}>
            <line
              x1={originX - 4}
              y1={groundY - m * scale}
              x2={originX + 4}
              y2={groundY - m * scale}
              stroke={colors.text}
              strokeWidth={1.5}
            />
            {m % 10 === 0 && (
              <text
                x={originX - 28}
                y={groundY - m * scale + 4}
                fontSize={14}
                fill={colors.text}
                opacity={0.65}
              >
                {m}
              </text>
            )}
          </g>
        ))}

        {/* 原点 */}
        <circle cx={originX} cy={groundY} r={4} fill={colors.text} />
        <text x={originX - 24} y={groundY + 24} fontSize={18} fill={colors.text} fontWeight={700}>
          O
        </text>

        {/* 发射角度弧线 */}
        {arcSweep > 0 && (
          <g opacity={0.9}>
            <path
              d={`M ${originX + arcRadius} ${groundY} A ${arcRadius} ${arcRadius} 0 0 0 ${
                originX + Math.cos(angleRad * arcSweep) * arcRadius
              } ${groundY - Math.sin(angleRad * arcSweep) * arcRadius}`}
              stroke={colors.vector}
              strokeWidth={2.5}
              fill="none"
            />
            <text
              x={originX + arcRadius * 0.55}
              y={groundY - 12}
              fontSize={20}
              fill={colors.vector}
              fontWeight={700}
            >
              θ = {angle}°
            </text>
          </g>
        )}

        {/* 炮台（发射装置），按发射角度旋转 */}
        <g transform={`translate(${originX}, ${groundY}) rotate(${-angle})`}>
          <rect x={-6} y={-38} width={44} height={16} fill="#475569" rx={3} />
          <rect x={-10} y={-42} width={10} height={24} fill="#1E293B" rx={2} />
          <circle cx={0} cy={-30} r={7} fill="#64748B" stroke="#1E293B" strokeWidth={2} />
        </g>
        <rect x={originX - 26} y={groundY - 8} width={52} height={14} fill="#334155" rx={3} />

        {/* 完整轨迹（幽灵预览，淡淡的底图） */}
        {showTrajectory && (
          <path
            d={generateFullGhostPath()}
            stroke={colors.trajectory}
            strokeWidth={1.5}
            fill="none"
            strokeDasharray="3,6"
            opacity={0.18}
          />
        )}

        {/* 实际走过的轨迹（高亮） */}
        {showTrajectory && (
          <path
            d={generateTrajectoryPath(clampedT)}
            stroke="url(#trajectoryGrad)"
            strokeWidth={4}
            fill="none"
            strokeLinecap="round"
            filter="url(#glow)"
          />
        )}

        {/* 最高点标记 */}
        {showMaxHeight && maxHeightPos && clampedT >= maxTime - 0.05 && (
          <g>
            <line
              x1={originX}
              y1={maxHeightPos.y}
              x2={maxHeightPos.x}
              y2={maxHeightPos.y}
              stroke={colors.highlight}
              strokeWidth={2}
              strokeDasharray="5,5"
              opacity={0.8}
            />
            <line
              x1={maxHeightPos.x}
              y1={groundY}
              x2={maxHeightPos.x}
              y2={maxHeightPos.y}
              stroke={colors.highlight}
              strokeWidth={2}
              strokeDasharray="5,5"
              opacity={0.8}
            />
            <circle
              cx={maxHeightPos.x}
              cy={maxHeightPos.y}
              r={8}
              fill="none"
              stroke={colors.highlight}
              strokeWidth={2.5}
            />
            <text
              x={maxHeightPos.x + 14}
              y={maxHeightPos.y - 16}
              fontSize={20}
              fill={colors.highlight}
              fontWeight={700}
            >
              最高点 H = {maxHeight.toFixed(1)} m
            </text>
          </g>
        )}

        {/* 射程标记 */}
        {showRange && landingPos && progress > 0.88 && (
          <g>
            <line
              x1={originX}
              y1={groundY + 48}
              x2={landingPos.x}
              y2={groundY + 48}
              stroke={colors.highlight}
              strokeWidth={2.5}
            />
            <polygon
              points={`${originX},${groundY + 43} ${originX + 10},${groundY + 48} ${originX},${groundY + 53}`}
              fill={colors.highlight}
            />
            <polygon
              points={`${landingPos.x},${groundY + 43} ${landingPos.x - 10},${groundY + 48} ${landingPos.x},${groundY + 53}`}
              fill={colors.highlight}
            />
            <text
              x={(originX + landingPos.x) / 2 - 55}
              y={groundY + 74}
              fontSize={20}
              fill={colors.highlight}
              fontWeight={700}
            >
              射程 R = {range.toFixed(1)} m
            </text>
          </g>
        )}

        {/* 速度向量分解 + 合速度 */}
        {showVectors && ballPos && clampedT < totalTime && clampedT > 0.05 && (
          <g>
            {/* Vx 水平分量 */}
            <line
              x1={ballPos.x}
              y1={ballPos.y}
              x2={ballPos.x + currentVx * vScale}
              y2={ballPos.y}
              stroke={colors.vector}
              strokeWidth={3}
              markerEnd="url(#arrowVec)"
              opacity={0.9}
            />
            <text
              x={ballPos.x + currentVx * vScale * 0.5 - 10}
              y={ballPos.y - 10}
              fontSize={18}
              fill={colors.vector}
              fontWeight={700}
            >
              Vx={currentVx.toFixed(1)}
            </text>

            {/* Vy 垂直分量 */}
            <line
              x1={ballPos.x}
              y1={ballPos.y}
              x2={ballPos.x}
              y2={ballPos.y - currentVy * vScale}
              stroke={colors.vector}
              strokeWidth={3}
              markerEnd="url(#arrowVec)"
              opacity={0.9}
            />
            <text
              x={ballPos.x + 10}
              y={ballPos.y - currentVy * vScale * 0.5}
              fontSize={18}
              fill={colors.vector}
              fontWeight={700}
            >
              Vy={currentVy.toFixed(1)}
            </text>

            {/* 合速度 V */}
            {resultantEnd && (
              <>
                <line
                  x1={ballPos.x}
                  y1={ballPos.y}
                  x2={resultantEnd.x}
                  y2={resultantEnd.y}
                  stroke="#DC2626"
                  strokeWidth={3.5}
                  markerEnd="url(#arrowResult)"
                />
                <text
                  x={resultantEnd.x + 8}
                  y={resultantEnd.y + 4}
                  fontSize={18}
                  fill="#DC2626"
                  fontWeight={800}
                >
                  V={currentSpeed.toFixed(1)}
                </text>
                {/* 虚线辅助线，构成平行四边形 */}
                <line
                  x1={ballPos.x + currentVx * vScale}
                  y1={ballPos.y}
                  x2={resultantEnd.x}
                  y2={resultantEnd.y}
                  stroke={colors.vector}
                  strokeWidth={1.5}
                  strokeDasharray="4,4"
                  opacity={0.6}
                />
                <line
                  x1={ballPos.x}
                  y1={ballPos.y - currentVy * vScale}
                  x2={resultantEnd.x}
                  y2={resultantEnd.y}
                  stroke={colors.vector}
                  strokeWidth={1.5}
                  strokeDasharray="4,4"
                  opacity={0.6}
                />
              </>
            )}
          </g>
        )}

        {/* 拖尾残影 */}
        {trailPositions.map((p, i) => (
          <circle
            key={`trail-${i}`}
            cx={p.x}
            cy={p.y}
            r={13 - i}
            fill={colors.ball}
            opacity={p.opacity}
          />
        ))}

        {/* 小球 */}
        {ballPos && (
          <g>
            <circle
              cx={ballPos.x}
              cy={ballPos.y}
              r={22}
              fill={colors.ball}
              opacity={0.25}
              filter="url(#glow)"
            />
            <circle cx={ballPos.x} cy={ballPos.y} r={14} fill="url(#ballGrad)" />
            <circle
              cx={ballPos.x - 4}
              cy={ballPos.y - 4}
              r={4}
              fill="#FEE2E2"
              opacity={0.9}
            />
          </g>
        )}

        {/* 落地冲击波 */}
        {showImpact && landingPos && (
          <g>
            <circle
              cx={landingPos.x}
              cy={landingPos.y}
              r={10 + impactProgress * 60}
              fill="none"
              stroke={colors.ball}
              strokeWidth={3}
              opacity={1 - impactProgress}
            />
            <circle
              cx={landingPos.x}
              cy={landingPos.y}
              r={5 + impactProgress * 35}
              fill="none"
              stroke={colors.vector}
              strokeWidth={2}
              opacity={(1 - impactProgress) * 0.8}
            />
            {[0, 60, 120, 180, 240, 300].map((deg) => {
              const rad = (deg * Math.PI) / 180;
              const len = 10 + impactProgress * 35;
              return (
                <line
                  key={`spark-${deg}`}
                  x1={landingPos.x}
                  y1={landingPos.y}
                  x2={landingPos.x + Math.cos(rad) * len}
                  y2={landingPos.y + Math.sin(rad) * len}
                  stroke={colors.ball}
                  strokeWidth={2}
                  opacity={1 - impactProgress}
                />
              );
            })}
          </g>
        )}
      </svg>

      {/* 阶段提示胶囊 */}
      <div
        style={{
          position: 'absolute',
          top: 138,
          left: '50%',
          transform: `translateX(-50%) scale(${0.9 + panelSpring * 0.1})`,
          opacity: panelSpring,
          padding: '6px 18px',
          borderRadius: 999,
          background: phaseColor,
          color: '#fff',
          fontSize: 18,
          fontWeight: 700,
          letterSpacing: 2,
          boxShadow: `0 4px 14px ${phaseColor}66`,
          marginTop: 30,
        }}
      >
        ● {phaseLabel}
      </div>

      {/* 左下：物理公式面板 */}
      <div
        style={{
          position: 'absolute',
          bottom: 350,
          left: 260,
          fontSize: 20,
          color: colors.text,
          background: 'rgba(255,255,255,0.75)',
          backdropFilter: 'blur(6px)',
          padding: '14px 20px',
          borderRadius: 14,
          border: '1px solid rgba(16,185,129,0.25)',
          boxShadow: '0 6px 20px rgba(0,0,0,0.06)',
          opacity: panelSpring,
          transform: `translateY(${(1 - panelSpring) * 20}px)`,
          lineHeight: 1.9,
          fontFamily: 'ui-monospace, SFMono-Regular, monospace',
        }}
      >
        <div style={{ fontSize: 20, color: colors.highlight, fontWeight: 700, letterSpacing: 2 }}>
          FORMULAS
        </div>
        <div>x(t) = v₀·cosθ · t</div>
        <div>y(t) = v₀·sinθ · t − ½·g·t²</div>
        <div style={{ color: colors.highlight }}>H = v₀²sin²θ / (2g) = {maxHeight.toFixed(1)} m</div>
        <div style={{ color: colors.highlight }}>R = v₀²sin2θ / g = {range.toFixed(1)} m</div>
        <div style={{ color: colors.highlight }}>T = 2v₀sinθ / g = {totalTime.toFixed(2)} s</div>
      </div>

      {/* 进度指示器 */}
      <div
        style={{
          position: 'absolute',
          bottom: 20,
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <div style={{ fontSize: 24, color: colors.text, opacity: 0.6, letterSpacing: 2 }}>
          {clampedT.toFixed(2)}s / {totalTime.toFixed(2)}s
        </div>
        <div
          style={{
            width: 320,
            height: 10,
            backgroundColor: 'rgba(209,213,219,0.8)',
            borderRadius: 4,
            overflow: 'hidden',
            boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.08)',
          }}
        >
          <div
            style={{
              width: `${progress * 100}%`,
              height: '100%',
              background: `linear-gradient(90deg, ${colors.trajectory}, ${colors.highlight}, ${colors.ball})`,
              transition: 'width 0.05s linear',
            }}
          />
        </div>
      </div>
    </AbsoluteFill>
  );
};

/**
 * 使用示例：
 *
 * <Composition
 *   id="ProjectileMotion"
 *   component={ProjectileMotionTemplate}
 *   durationInFrames={300}
 *   fps={30}
 *   width={1920}
 *   height={1080}
 *   defaultProps={{
 *     v0: 20,
 *     angle: 45,
 *     g: 9.8,
 *     showVectors: true,
 *     showTrajectory: true,
 *     showMaxHeight: true,
 *     showRange: true,
 *     animationDuration: 5,
 *     colors: {
 *       background: '#F0F9FF',
 *       ground: '#059669',
 *       trajectory: '#3B82F6',
 *       ball: '#EF4444',
 *       vector: '#F59E0B',
 *       text: '#1F2937',
 *       highlight: '#10B981',
 *     },
 *   }}
 * />
 */
